import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTracksByTitle } from '../../data/tracks';

/* ══════════════════════════════════════
   TRACK MULTI-SELECT DROPDOWN
   ══════════════════════════════════════ */
export default function TrackMultiSelect({ titleId, selected, onChange }) {
  const tracks = useMemo(() => getTracksByTitle(titleId), [titleId]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = search
    ? tracks.filter(t => t.toLowerCase().includes(search.toLowerCase()))
    : tracks;

  const toggle = (track) => {
    if (selected.includes(track)) onChange(selected.filter(t => t !== track));
    else onChange([...selected, track]);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="form-select"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: selected.length === 0 ? 'var(--text-muted)' : 'var(--text)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected.length === 0 ? 'Select tracks...' : selected.length === 1 ? selected[0] : `${selected.length} tracks selected`}
        </span>
        <span style={{ fontSize: 10, marginLeft: 8, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)',
          borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          maxHeight: 300, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px 4px', borderBottom: '1px solid var(--border)' }}>
            <input
              className="form-input"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tracks..." autoFocus
              style={{ marginBottom: 6, fontSize: 12, padding: '6px 10px' }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => onChange([...tracks])}
                style={{ fontSize: 11, padding: '2px 8px' }}>Select All</button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => onChange([])}
                style={{ fontSize: 11, padding: '2px 8px' }}>Clear</button>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', alignSelf: 'center' }}>
                {selected.length}/{tracks.length}
              </span>
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
            {filtered.map(track => {
              const isSelected = selected.includes(track);
              return (
                <div
                  key={track} onClick={() => toggle(track)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', cursor: 'pointer', borderRadius: 4, fontSize: 13,
                    background: isSelected ? 'var(--teal-soft)' : 'transparent',
                    color: isSelected ? 'var(--teal)' : 'var(--text)',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                    border: isSelected ? '2px solid var(--teal)' : '2px solid var(--border-strong)',
                    background: isSelected ? 'var(--teal)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: '#fff', fontWeight: 700,
                  }}>
                    {isSelected ? '✓' : ''}
                  </span>
                  {track}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {selected.map(t => (
            <span key={t} className="badge badge-teal" style={{ fontSize: 11, cursor: 'pointer' }}
              onClick={() => toggle(t)} title="Click to remove">
              {t} ×
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
